<template>
  <div class="preselection-rule">
    <div class="criteria-name">{{ $t("AdvancedSearch.SearchType." + vm.localModel.searchType) }}</div>
    <div class="criteria-data">
      <div class="preselection-data">
        <b-btn variant="link" class="attribute-value" @click="modals.isOpen = true">{{ vm.preselectionInfo ? vm.preselectionInfo?.name : "" }}</b-btn>
        <div class="connection-text" v-if="showAttributeGroupsPath && vm.preselectionInfo?.paths && vm.preselectionInfo.paths.length">
          {{ $t("in") }}
        </div>
        <div class="attribute-group" v-if="showAttributeGroupsPath && vm.preselectionInfo?.paths">
          <PathPresenter v-model="vm.preselectionInfo.paths" />
        </div>
      </div>
      <div class="criteria-manipulation">
        <div class="criteria-operator">
          <MSelect v-model="vm.localModel.operator" :items="vm.ops" textField="name" valueField="id" data-cy="rule-operator" />
        </div>
        <div class="criteria-value" v-if="valueInputRequired">
          <component :is="getValueSelectionComponentByType()" can-be-none v-model="vm.localModel.value" @delete-rule="emits('deleteRule')"></component>
        </div>
      </div>
    </div>
    <div class="criteria-meta-info">
      <div class="query-builder-rule-language-selection">
        <MSelect
          v-if="languageSelectionRequired"
          v-model="vm.localModel.dataLanguage"
          :items="languageSelection"
          textField="name"
          valueField="id"
          data-cy="rule-language" />
      </div>
      <div class="query-builder-rule-actions">
        <b-btn class="btn-icon mono" @click="emits('deleteRule')" data-cy="rule-clear"> <i class="mdp-Close"></i> </b-btn>
      </div>
    </div>

    <component
      v-model="modals.isOpen"
      :is="getAttributeTypeComponent()"
      :attribute-type="vm.localModel.searchType == 'textAttribute' ? 'text' : 'table'"
      :selected-entry-id="vm.preselectionInfo?.id"
      :selected-path="vm.preselectionInfo?.paths"
      @confirm="onSelection"
      @hide="onAttributeCancel" />
  </div>
</template>

<script setup lang="ts">
import MSelect from "@/BasicComponentLibrary/MSelect.vue";
import { getTranslatedSelectionOpsList, requiresOperatorLanguageSelection, requiresOperatorValueInput } from "@AdvancedSearch/Util/opsLookUp";
import ERPAttributeSelectionModal from "@common/components/MultiLevelNavigation/ERPAttributeSelectionModal.vue";
import GlobalAttributeSelectionModal from "@common/components/MultiLevelNavigation/GlobalAttributeSelectionModal.vue";
import MediaAssetValueSelectionModal from "@common/components/MultiLevelNavigation/MediaAssetValueSelectionModal.vue";
import TextTableAttributeSelectionModal from "@common/components/MultiLevelNavigation/TextTableAttributeSelectionModal.vue";
import PathPresenter from "@common/components/PathPresenter.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import {
  AllowedOps,
  DataUITypes,
  OpsSelectionModel,
  QueryAdditionalOperatorSupport,
  QueryBuilderRule,
  SearchTypes
} from "@medapro-types/AdvancedSearch/components/QueryBuilder";
import { AttributeDataType } from "@medapro-types/common/common-enum";
import { Branch, MultiLevelNavigationConfirmModel, MultiLevelNavigationSelectionModel } from "@medapro-types/common/components/MultiLevelNavigation";
import attributesApi from "@services/attributes.js";
import erpAttributesApi from "@services/erpAttributes.js";
import globalAttributesApi from "@services/globalAttributes.js";
import mediaAssets from "@services/mediaAssets.js";
import { BaseIds } from "@util/enums/base-ids";
import _ from "lodash";
import { computed, inject, nextTick, onMounted, reactive, Ref, unref, watch } from "vue";
import { useI18n } from "vue-i18n";
import BoolRule from "../QueryRules/BoolRule.vue";
import DateRule from "../QueryRules/DateRule.vue";
import NumberRule from "../QueryRules/NumberRule.vue";
import StringRule from "../QueryRules/StringRule.vue";

//#region General
const props = defineProps<{ value: QueryBuilderRule; languageRequired?: boolean | null; additionalOperatorSupport?: QueryAdditionalOperatorSupport }>();
const emits = defineEmits(["input", "deleteRule", "refreshBackground"]);

const { t: $t } = useI18n();
const isBusy = inject<Ref<boolean>>("advancedSearchIsBusy");
//#endregion

//#region Store
const { primaryLanguageCode, localizedLanguages: localizedLanguages } = useContentLanguages();
//#endregion

//#region Languages
const languageSelection = computed(() => {
  const result = [
    { id: "", name: `<${$t("all")}>` },
    ...(unref(localizedLanguages)?.map((x) => ({
      id: x.languageCode,
      name: x.localizedName
    })) ?? [])
  ];

  return result;
});

//#endregion

//#region Busy
const setBusyState = (busyValue: boolean) => {
  if (isBusy) {
    isBusy.value = busyValue;
  }
};
//#endregion

//#region Modal
const getAttributeTypeComponent = () => {
  switch (vm.localModel.searchType) {
    case "tableAttribute":
    case "textAttribute":
      return TextTableAttributeSelectionModal;
    case "globalAttribute":
      return GlobalAttributeSelectionModal;
    case "erpAttribute":
      return ERPAttributeSelectionModal;
    case "mediaAssetValue":
      return MediaAssetValueSelectionModal;
  }
};

const allEntry: MultiLevelNavigationSelectionModel = {
  id: null,
  name: `<${$t("all")}>`,
  type: "all",
  dataType: AttributeDataType.AttributeDataTypeAlphanumerical
};

const loadPreselectionInfo = async (searchType: SearchTypes) => {
  setBusyState(true);
  if (vm.localModel.attributeId === null) {
    vm.preselectionInfo = allEntry;
  } else {
    if (vm.localModel.attributeId && vm.localModel.attributeId != "unselected" && !vm.preselectionInfo) {
      let fetchedList: any[] = [];
      let baseNodeIndex: number;
      let preselectionInfo: Record<string, any> | null | undefined = {};
      switch (searchType) {
        case "tableAttribute":
        case "textAttribute":
          fetchedList = await attributesApi.getDetails(primaryLanguageCode.value, [vm.localModel.attributeId]);

          preselectionInfo = _.first(fetchedList);
          if (preselectionInfo && preselectionInfo.paths) {
            baseNodeIndex = _.findIndex(preselectionInfo.paths, (x: any) => x.id == BaseIds.BaseAttributeGroup);
            if (baseNodeIndex != -1) preselectionInfo.paths.splice(baseNodeIndex, 1);
          }
          break;
        case "erpAttribute":
          fetchedList = await erpAttributesApi.getDetails(primaryLanguageCode.value, [vm.localModel.attributeId]);
          preselectionInfo = _.first(fetchedList);
          break;
        case "globalAttribute":
          fetchedList = await globalAttributesApi.getDetails(primaryLanguageCode.value, [vm.localModel.attributeId]);
          preselectionInfo = _.first(fetchedList);
          break;
        case "mediaAssetValue":
          fetchedList = await mediaAssets.getDetails(primaryLanguageCode.value, [vm.localModel.attributeId]);
          preselectionInfo = _.first(fetchedList);
          if (preselectionInfo) preselectionInfo.dataType = AttributeDataType.AttributeDataTypeAlphanumerical;
          break;
      }
      updatePreselectionInfoPaths(preselectionInfo?.paths);
      vm.preselectionInfo = preselectionInfo;
    } else vm.localModel.attributeId = "unselected";
  }
  setBusyState(false);
};

const onSelection = (event: MultiLevelNavigationConfirmModel) => {
  if (event.selection) {
    vm.localModel.attributeId = event.selection.id;
    vm.preselectionInfo = event.selection;
    setLocalDataValueType();
    if (vm.localDataValueType == "date") {
      vm.localModel.value = "unselected";
    }
  }
  if (event.paths) {
    const paths = _.cloneDeep(event.paths);
    updatePreselectionInfoPaths(paths);
    if (vm.preselectionInfo) Object.assign(vm.preselectionInfo, { paths });
  }
};

const updatePreselectionInfoPaths = (paths: Branch[]) => {
  const homeEntry: Branch = {
    id: "",
    name: null,
    fallbackName: null,
    depth: 0,
    icon: "Home",
    parentId: null
  };

  if (paths) {
    paths.forEach((x) => delete x.icon);
    if (paths.length == 0) {
      if (vm.localModel.searchType === "textAttribute") {
        homeEntry.name = $t("text.attributes");
      } else if (vm.localModel.searchType === "tableAttribute") {
        homeEntry.name = $t("table.attributes");
      }
    }
    paths.unshift(homeEntry);
  }
};

const onAttributeCancel = () => {
  if (vm.localModel.attributeId == "unselected") emits("deleteRule");
};

const getValueSelectionComponentByType = () => {
  switch (vm.localDataValueType) {
    case "numeric":
      return NumberRule;
    case "logical":
      return BoolRule;
    case "date":
      return DateRule;
    case "interval":
      return NumberRule;

    default:
      return StringRule;
  }
};
//#endregion

//#region ViewModel
type ViewModel = {
  localModel: QueryBuilderRule;
  ops: OpsSelectionModel[];
  localDataValueType: DataUITypes | null;
  preselectionInfo: Record<string, any> | null | undefined;
};
const vm = reactive<ViewModel>({
  localModel: props.value,
  ops: [],
  localDataValueType: null,
  preselectionInfo: null
});

const modals = reactive({ isOpen: false });
//#endregion

//#region Controle
const showAttributeGroupsPath = computed(() => {
  return vm.localModel.searchType != "globalAttribute" && vm.localModel.searchType != "mediaAssetValue" && vm.localModel.attributeId !== null;
});

const languageSelectionRequired = computed(() => {
  return props.languageRequired && requiresOperatorLanguageSelection(vm.localModel.operator);
});

const valueInputRequired = computed(() => {
  return requiresOperatorValueInput(vm.localModel.operator);
});
//#endregion

//#region LocalDataValueType

const setLocalDataValueType = () => {
  if (vm.preselectionInfo) {
    switch (vm.preselectionInfo.dataType) {
      case AttributeDataType.AttributeDataTypeAlphanumerical:
        vm.localDataValueType = "alphanumeric";
        break;
      case AttributeDataType.AttributeDataTypeDate:
        vm.localDataValueType = "date";
        break;
      case AttributeDataType.AttributeDataTypeInterval:
        vm.localDataValueType = "interval";
        break;
      case AttributeDataType.AttributeDataTypeLogical:
        vm.localDataValueType = "logical";
        break;
      case AttributeDataType.AttributeDataTypeNumerical:
        vm.localDataValueType = "numeric";
        break;
    }
    if (vm.localDataValueType && !vm.localModel.operator) {
      const firstOperator = ((_.first(vm.ops)?.id ?? vm.localDataValueType !== "interval") ? "equals" : "includes") satisfies AllowedOps;
      vm.localModel.operator = firstOperator;
    }
  }
};

watch(
  [() => vm.localDataValueType, () => vm.localModel.value],
  ([type]) => {
    if (type) {
      if (vm.localDataValueType === "date" && vm.localModel.value === "")
        vm.ops = getTranslatedSelectionOpsList(type, props.additionalOperatorSupport).filter((o) => o.id === "equals" || o.id === "equalsNot");
      else vm.ops = getTranslatedSelectionOpsList(type, props.additionalOperatorSupport);
    }
  },
  { immediate: true }
);
//#endregion

//#region LifeCycle
onMounted(async () => {
  await loadPreselectionInfo(vm.localModel.searchType);
  setLocalDataValueType();
  if (vm.localModel.dataLanguage === undefined) {
    vm.localModel.dataLanguage = "";
  }
  nextTick(() => {
    if (vm.localModel.attributeId == "unselected" || vm.localModel.value == "unselected") {
      modals.isOpen = true;
    }
  });
});
//#endregion
</script>
